import { useState, useEffect } from 'react';
import { ArrowLeft, Train, ExternalLink, Calendar, Clock, ArrowRight, AlertCircle, Loader2, Database, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const AnreiseAutozugPage = () => {
  const navigate = useNavigate();
  const [direction, setDirection] = useState('nach');
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sourceNote, setSourceNote] = useState('');
  
  // Neue State-Variablen für Datenquellen-Status
  const [dataStatus, setDataStatus] = useState(null); // "live" | "fallback" | "error"
  const [dataSource, setDataSource] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const providers = [
    {
      id: 'rot',
      name: 'Roter Autozug (DB Sylt Shuttle)',
      color: '#C62828',
      bgColor: '#FFEBEE',
      url: 'https://www.syltshuttle.de/syltshuttle-de/fahrplan',
    },
    {
      id: 'blau',
      name: 'Blauer Autozug',
      color: '#0E4C92',
      bgColor: '#E3F2FD',
      url: 'https://www.autozug-sylt.de/de/fahrplan/',
    }
  ];

  // Daten laden
  useEffect(() => {
    const fetchSchedule = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/anreise/autozug`, {
          params: { direction, date: selectedDate }
        });
        setSchedule(response.data.data || []);
        setSourceNote(response.data.source_note || '');
        
        // Neue Felder für Datenquellen-Status
        setDataStatus(response.data.status || null);
        setDataSource(response.data.source || '');
        setLastUpdated(response.data.last_updated || null);
      } catch (err) {
        console.error('Fehler beim Laden:', err);
        setError('Fahrplan konnte nicht geladen werden');
        setSchedule([]);
        setDataStatus(null);
        setDataSource('');
        setLastUpdated(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, [direction, selectedDate]);

  // v2.8.0: Immer kompletten Tagesfahrplan anzeigen (kein Zeitfilter)
  const filteredSchedule = schedule;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0E4C92] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zum Chat
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Train className="w-8 h-8" />
            <h1 className="text-2xl font-bold">Autozug Sylt – Fahrzeiten</h1>
          </div>
          <p className="text-white/80">Aktuelle Verbindungen nach und von Sylt</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Hinweis */}
        <p className="text-sm text-gray-500 mb-6">
          Alle Angaben ohne Gewähr. Maßgeblich sind die Angaben der Anbieter.
        </p>

        {/* Filter & Anbieter in einem Block */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Linke Seite: Filter */}
            <div>
              <h2 className="font-semibold text-gray-900 mb-4">Verbindung suchen</h2>
              
              {/* Richtung */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Richtung</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="nach"
                      checked={direction === 'nach'}
                      onChange={(e) => setDirection(e.target.value)}
                      className="w-4 h-4 text-[#0E4C92]"
                    />
                    <span>Nach Sylt</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="direction"
                      value="von"
                      checked={direction === 'von'}
                      onChange={(e) => setDirection(e.target.value)}
                      className="w-4 h-4 text-[#0E4C92]"
                    />
                    <span>Von Sylt</span>
                  </label>
                </div>
              </div>

              {/* Datum */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Datum
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0E4C92] focus:border-transparent"
                />
              </div>
            </div>

            {/* Rechte Seite: Anbieter-Links */}
            <div>
              <h2 className="font-semibold text-gray-900 mb-4">Fahrpläne der Anbieter</h2>
              <div className="space-y-2">
                {providers.map((provider) => (
                  <a
                    key={provider.id}
                    href={provider.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                    style={{ borderLeftWidth: '3px', borderLeftColor: provider.color }}
                  >
                    <Train className="w-5 h-5" style={{ color: provider.color }} />
                    <span className="flex-1 text-sm font-medium" style={{ color: provider.color }}>
                      {provider.name}
                    </span>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* HAUPTBEREICH: Fahrzeiten-Liste */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Fahrzeiten
              </h2>
              <p className="text-sm text-gray-500">
                für {formatDate(selectedDate)} – {direction === 'nach' ? 'Nach Sylt' : 'Von Sylt'}
              </p>
            </div>
            {sourceNote && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                {sourceNote}
              </span>
            )}
          </div>

          {/* Datenqualitäts-Hinweis (optional, kontextabhängig) */}
          {!loading && (() => {
            const isFuture = selectedDate !== today;
            const isNotLive = dataStatus && dataStatus !== 'live';

            if (isFuture) return (
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700" data-testid="hint-planzeiten">
                <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Planzeiten – keine Echtzeitdaten für zukünftige Tage</span>
              </div>
            );
            if (isNotLive) return (
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700" data-testid="hint-no-live">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Echtzeit nicht verfügbar – Anzeige ggf. Planzeiten/Demo</span>
              </div>
            );
            return null;
          })()}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0E4C92]" />
              <span className="ml-3 text-gray-600">Lade Fahrplan...</span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <p className="text-red-700">{error}</p>
              <p className="text-sm text-red-600 mt-2">
                Bitte prüfen Sie die Zeiten direkt bei den Anbietern.
              </p>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredSchedule.length === 0 && (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <p className="text-gray-600">
                Für den gewählten Zeitraum sind keine weiteren Verbindungen verfügbar.
              </p>
            </div>
          )}

          {/* Schedule List */}
          {!loading && !error && filteredSchedule.length > 0 && (
            <div className="space-y-3">
              {filteredSchedule.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-4 p-4 rounded-lg border hover:bg-gray-50 transition-colors"
                  style={{ borderLeftWidth: '4px', borderLeftColor: item.provider_color }}
                >
                  {/* Uhrzeit */}
                  <div className="w-20">
                    <span className="text-xl font-bold text-gray-900">{item.time}</span>
                    {item.delay && (
                      <span className="block text-sm font-medium text-amber-600">
                        {item.delay}
                      </span>
                    )}
                  </div>

                  {/* Icon & Anbieter */}
                  <div className="flex items-center gap-3 flex-1">
                    <Train className="w-5 h-5" style={{ color: item.provider_color }} />
                    <div>
                      <p className="font-medium" style={{ color: item.provider_color }}>
                        {item.provider}
                      </p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <ArrowRight className="w-3 h-3" />
                        {item.route}
                      </p>
                      {item.delay_message && (
                        <p className="text-xs text-amber-600 mt-0.5" data-testid={`delay-message-${idx}`}>
                          {item.delay_message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Status Badge - dynamisch basierend auf DB Timetables Daten */}
                  {(() => {
                    // Logik für Status-Badge
                    const cancelled = item.cancelled === true;
                    const delayMinutes = item.delay_minutes ?? 0;
                    const delayMessage = item.delay_message || '';
                    
                    if (cancelled) {
                      return (
                        <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-medium">
                          fällt aus
                        </span>
                      );
                    } else if (item.status === 'Verzögerung' && delayMessage) {
                      return (
                        <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium" title={delayMessage}>
                          Verzögerung
                        </span>
                      );
                    } else if (delayMinutes >= 5) {
                      return (
                        <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium">
                          +{delayMinutes} min
                        </span>
                      );
                    } else {
                      return (
                        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                          planmäßig
                        </span>
                      );
                    }
                  })()}
                </div>
              ))}
            </div>
          )}
          
          {/* Datenquellen-Hinweis (unauffällig unter der Liste) */}
          {!loading && dataStatus && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  {dataStatus === 'live' && (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      <span className="text-green-600">Datenquelle: Live-Daten</span>
                    </>
                  )}
                  {dataStatus === 'fallback' && (
                    <>
                      <Database className="w-3.5 h-3.5 text-gray-400" />
                      <span>Datenquelle: Demo-Daten</span>
                    </>
                  )}
                  {dataStatus === 'error' && (
                    <>
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-amber-600">Datenquelle: Fehler (DB)</span>
                    </>
                  )}
                </div>
                {lastUpdated && (
                  <span>
                    Stand: {new Date(lastUpdated).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-2">Gut zu wissen</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Die Autozüge verkehren mehrmals täglich zwischen Niebüll und Westerland</li>
            <li>• Fahrtzeit ca. 35-40 Minuten</li>
            <li>• Online-Buchung wird empfohlen, besonders in der Hauptsaison</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AnreiseAutozugPage;
